/**
 * Global imperative confirm() replacement built on shadcn/Radix AlertDialog.
 *
 * WHY: `window.confirm()` is silently blocked by modern browsers when the
 * call is fired from inside a Radix <Dialog> or after certain user gestures.
 * We had at least one production regression (Approve & Generate Employee ID
 * button) where clicks did nothing because `window.confirm` returned false
 * without ever showing a prompt.
 *
 * USAGE
 *   import { useConfirm } from "@/components/ui/confirm-dialog";
 *   const confirm = useConfirm();
 *   if (!(await confirm("Delete this item?"))) return;
 *   // …proceed
 *
 * The <ConfirmDialogProvider /> must be mounted once at the app root
 * (see App.js).
 */
import { createContext, useCallback, useContext, useRef, useState } from "react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./alert-dialog";

const ConfirmContext = createContext(null);

export function ConfirmDialogProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState({});
  const resolverRef = useRef(null);

  const confirm = useCallback((messageOrOpts, extra = {}) => {
    const o = typeof messageOrOpts === "string"
      ? { message: messageOrOpts, ...extra }
      : { ...messageOrOpts };
    setOpts({
      title: "Are you sure?",
      confirmLabel: "Confirm",
      cancelLabel: "Cancel",
      destructive: false,
      ...o,
    });
    setOpen(true);
    return new Promise((resolve) => { resolverRef.current = resolve; });
  }, []);

  const settle = (val) => {
    setOpen(false);
    const r = resolverRef.current;
    resolverRef.current = null;
    if (r) r(val);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={open} onOpenChange={(v) => { if (!v) settle(false); }}>
        <AlertDialogContent data-testid="global-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{opts.title}</AlertDialogTitle>
            {opts.message && (
              <AlertDialogDescription className="whitespace-pre-wrap">{opts.message}</AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => settle(false)} data-testid="global-confirm-cancel">
              {opts.cancelLabel}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => settle(true)}
              className={opts.destructive ? "bg-rose-600 hover:bg-rose-700 text-white" : ""}
              data-testid="global-confirm-yes"
            >
              {opts.confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

/** Hook returning an async confirm() function. Falls back to window.confirm
 *  if the provider is missing so the caller never crashes. */
export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  return ctx || ((msg) => Promise.resolve(window.confirm(typeof msg === "string" ? msg : msg?.message || "")));
}
